import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Loading } from './Loading';
import { preloadRoute } from '../navigation/routes';

const MIN_LOADING_MS = 400;
const FADE_MS = 200;
const IDLE_PRELOAD_DELAY_MS = 2500;
const POPULAR_PATHS = ['/opportunitiestraining', '/trainings', '/opportunitiesinterships'];

/**
 * Shows a full-screen loading state as soon as the route (pathname) changes.
 * Fades out only when leaving.
 */
export const RouteChangeLoader = ({ children }) => {
  const { pathname } = useLocation();
  const [showLoader, setShowLoader] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const prevPathnameRef = useRef(pathname);
  const isInitialMount = useRef(true);

  useLayoutEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevPathnameRef.current = pathname;
      return;
    }
    if (pathname === prevPathnameRef.current) return;
    prevPathnameRef.current = pathname;
    setLeaving(false);
    setShowLoader(true);
  }, [pathname]);

  // After min time, fade out then unmount
  useEffect(() => {
    if (!showLoader) return;
    const t1 = setTimeout(() => setLeaving(true), MIN_LOADING_MS);
    const t2 = setTimeout(() => {
      setShowLoader(false);
      setLeaving(false);
    }, MIN_LOADING_MS + FADE_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showLoader]);

  // Idle preload: after delay, preload popular routes so first click is faster
  useEffect(() => {
    const id = setTimeout(() => {
      const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 100));
      schedule(() => {
        POPULAR_PATHS.forEach((p) => preloadRoute(p));
      });
    }, IDLE_PRELOAD_DELAY_MS);
    return () => clearTimeout(id);
  }, []);

  const className = ['route-change-loader', leaving && 'route-change-loader--leaving'].filter(Boolean).join(' ');

  return (
    <>
      {showLoader && (
        <div className={className} aria-hidden="true">
          <Loading />
        </div>
      )}
      {children}
    </>
  );
};
